"use client";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Card } from "@/components/ui/card";
import { landingCopy } from "./copy";
import { Quote } from "lucide-react";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import Image from "next/image";
import { LandingSectionBadge } from "./LandingSectionBadge";

export default function SocialProof() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const testimonialVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.42, 0, 0.58, 1] as const, // easeOut cubic bezier
      },
    },
  };

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-b from-background via-blue-50/30 to-background dark:via-blue-950/10">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Customer Logos Strip */}
        <motion.div
          className="mb-16"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.55 }}
        >
          <div className="mb-8 flex justify-center">
            <LandingSectionBadge className="mb-0">Trusted partners</LandingSectionBadge>
          </div>
          <p className="mb-6 text-center text-sm text-muted-foreground">
            Trusted by growing businesses
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-16 gap-y-8 py-2">
            {landingCopy.trustedLogos.map((logo, i) => (
              <div key={`${logo.src}-${i}`} className="flex h-16 items-center sm:h-20 lg:h-24">
                <Image
                  src={logo.src}
                  alt={logo.alt}
                  width={logo.width || 200}
                  height={logo.height || 80}
                  className="max-h-full w-auto object-contain opacity-85 grayscale transition-all duration-300 hover:opacity-100 hover:grayscale-0"
                  quality={85}
                  unoptimized
                />
              </div>
            ))}
          </div>
        </motion.div>

        <div ref={ref} className="mx-auto max-w-5xl">
          <div className="mb-8 flex justify-center">
            <LandingSectionBadge className="mb-0">Customer stories</LandingSectionBadge>
          </div>
          <motion.div
            className="grid gap-6 md:grid-cols-2 lg:gap-8"
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
          {landingCopy.testimonials.map((testimonial, index) => (
            <motion.div key={index} variants={testimonialVariants} custom={index}>
              <Card className="group relative flex h-full flex-col overflow-hidden border border-border/70 bg-gradient-to-br from-card/95 via-card/90 to-primary/[0.03] p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary/25 hover:shadow-lg lg:p-8">
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/[0.04] via-transparent to-cyan-500/[0.05] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                <div className="relative z-10 flex min-h-0 flex-1 flex-col">
                {/* Client Logo Header */}
                {testimonial.logo && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.2 + 0.1, duration: 0.4 }}
                    className="mb-6 pb-6 border-b border-border/50"
                  >
                    <div className="h-12 lg:h-14 w-auto flex items-center justify-start">
                      <Image
                        src={testimonial.logo.src}
                        alt={testimonial.logo.alt}
                        width={testimonial.logo.width || 150}
                        height={testimonial.logo.height || 60}
                        className="object-contain opacity-90 group-hover:opacity-100 transition-opacity duration-300 max-h-full"
                        quality={100}
                        unoptimized
                      />
                    </div>
                  </motion.div>
                )}
                
                {/* Quote Icon and Testimonial Content */}
                <div className="flex-1 flex flex-col">
                  <motion.div
                    initial={{ opacity: 0, scale: 0 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.2 + 0.3, duration: 0.3 }}
                    className="mb-4"
                  >
                    <Quote className={`h-6 w-6 ${index === 0 ? 'text-blue-500' : 'text-cyan-500'} opacity-60`} />
                  </motion.div>
                  
                  {/* Testimonial Quote */}
                  <p className="text-foreground mb-6 leading-relaxed text-sm lg:text-base flex-1">
                    &quot;{testimonial.quote}&quot;
                  </p>
                  
                  {/* Author Info */}
                  <div className="flex items-center gap-4 pt-4 border-t border-border/30 mt-auto">
                    <Avatar className="h-11 w-11 ring-2 ring-primary/10 flex-shrink-0">
                      <AvatarFallback className="bg-primary/10 text-primary font-semibold text-sm">
                        {testimonial.avatar}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-foreground text-sm lg:text-base">
                        {testimonial.author}
                      </p>
                      <p className="text-xs lg:text-sm text-muted-foreground">
                        {testimonial.role}
                        {testimonial.company && (
                          <span className="text-muted-foreground/70">, {testimonial.company}</span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
                </div>
              </Card>
            </motion.div>
          ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

